import { Table } from '@allurion/ui';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { NavigateOptions } from 'react-router-dom';

import { InlineLoader } from 'src/components/ui/InlineLoader';
import { ParsedClinicData } from 'src/domain/Clinic';
import { ClinicProvider, useManageClinicsProviders } from 'src/hooks/useManageClinicsProviders';
import { useTableSort } from 'src/ui/useTableSort';

import { Pagination } from '../../components/Pagination';
import { Spacer } from '../shared-page-elements';

import translations from './ManageUsers.translations';

type Props = {
  search: string;
  clinics?: ParsedClinicData[];
  onRowClick: (providerId: string, options?: NavigateOptions) => void;
  onResendInviteClick: (options: NavigateOptions) => void;
};

export function ManageUsersTable({ search, clinics, onRowClick, onResendInviteClick }: Props) {
  const intl = useIntl();
  const { isLoading, providers } = useManageClinicsProviders(clinics);
  const [page, setPage] = useState(1);
  const defaultPageSize = 10;
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const filteredProviders = useMemo(
    () =>
      providers.filter((provider) => {
        const { FirstName, LastName, EmailAddress, ClinicLabel } = provider;

        return (
          FirstName.toLowerCase().includes(search.toLowerCase()) ||
          LastName.toLowerCase().includes(search.toLowerCase()) ||
          EmailAddress.toLowerCase().includes(search.toLowerCase()) ||
          ClinicLabel.toLowerCase().includes(search.toLowerCase())
        );
      }),
    [providers, search]
  );
  const {
    sortedData: sortedProviders,
    sortBy,
    setSortBy,
  } = useTableSort<ClinicProvider>(filteredProviders, '+FirstName', {});

  const paginatedProviders = sortedProviders.slice((page - 1) * pageSize, page * pageSize);
  const lastPage = Math.ceil(sortedProviders.length / pageSize);

  if (isLoading && providers.length === 0) {
    return <InlineLoader />;
  }

  const handleRowClick = (e: React.MouseEvent<HTMLAnchorElement>, provider: ClinicProvider) => {
    e.stopPropagation();
    e.preventDefault();
    onRowClick(provider.ProviderID, { state: { user: provider } });
  };

  return (
    <>
      <Table
        columns={[
          {
            title: intl.formatMessage(translations.manageUsersTableFirstName),
            field: 'FirstName',
            formatter(_, provider) {
              return (
                <a href="" onClick={(e) => handleRowClick(e, provider)}>
                  {provider.FirstName}
                </a>
              );
            },
          },
          {
            title: intl.formatMessage(translations.manageUsersTableLastName),
            field: 'LastName',
            formatter(_, provider) {
              return (
                <a href="" onClick={(e) => handleRowClick(e, provider)}>
                  {provider.LastName}
                </a>
              );
            },
          },
          {
            title: intl.formatMessage(translations.manageUsersTableEmail),
            field: 'EmailAddress',
          },
          {
            title: intl.formatMessage(translations.manageUsersTableClinic),
            field: 'ClinicLabel',
          },
          {
            title: intl.formatMessage(translations.manageUsersTableRole),
            field: 'RoleID',
            formatter(_, row) {
              return formatRole(row);
            },
          },
        ]}
        data={paginatedProviders}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      <Spacer />
      <Pagination
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        lastPage={lastPage}
        defaultPageSize={defaultPageSize}
        pageSizeLabel={intl.formatMessage({
          id: 'manage-users-table.page-size-label',
          defaultMessage: 'Users per page',
        })}
      />
    </>
  );

  function formatRole(provider: ClinicProvider) {
    const { IsVerified } = provider;
    const label = {
      '1': 'Super User',
      '2': intl.formatMessage(translations.manageUsersAdmin),
      '3': intl.formatMessage(translations.manageUsersStaff),
      '4': intl.formatMessage(translations.manageUsersCoach),
    };

    return (
      <>
        {label[provider.RoleID as keyof typeof label]}
        {!IsVerified && (
          <>
            &nbsp;-&nbsp;
            {intl.formatMessage(translations.manageUsersTableInvited)}
            &nbsp;-&nbsp;
            <a href="" onClick={(e) => handleResendInvitationLink(e, provider)}>
              {intl.formatMessage(translations.manageUsersTableResendInvite)}
            </a>
          </>
        )}
      </>
    );
  }

  function handleResendInvitationLink(
    e: React.MouseEvent<HTMLAnchorElement>,
    provider: ClinicProvider
  ) {
    e.preventDefault();
    e.stopPropagation();

    onResendInviteClick({ state: { user: provider } });
  }
}
